// Level 11 interval construction + inversion: shared by authoring, display and grading.
import { intervalLabel, parseRequestedInterval, transposeRequestedInterval } from '../editor/intervalConstructionModel.js';

const LETTERS = 'CDEFGAB';
const NATURAL = [0, 2, 4, 5, 7, 9, 11];
const INVERT_QUALITY = { MAJ: 'MIN', MIN: 'MAJ', PER: 'PER', AUG: 'DIM', DIM: 'AUG' };
export const INVERSION_QUALITY_OPTIONS = ['MAJ', 'min', 'PER', 'AUG', 'dim'];
export const INVERSION_SIZE_OPTIONS = [1,2,3,4,5,6,7,8];

// Display only: preserve case-insensitive interval parsing and internal quality codes.
// TMTA prints MAJ/PER/AUG in capitals, but min/dim in lowercase.
export function formatInversionIntervalLabel(value) {
  const parsed = parseRequestedInterval(value);
  if (!parsed) return String(value || '');
  return intervalLabel(parsed).replace(/^(MIN|DIM)(?=\s)/, quality => quality.toLowerCase());
}

export function parseInversionPitch(pitch) {
  const m = String(pitch || '').trim().replaceAll('♯','#').replaceAll('♭','b').replaceAll('♮','n').match(/^([A-Ga-g])(bb|##|b|#|n)?([0-8])$/);
  if (!m) return null;
  const index = LETTERS.indexOf(m[1].toUpperCase());
  const alteration = (m[2] || '').replace('n','').split('').reduce((v,c)=>v+(c==='#'?1:-1),0);
  return {letter:LETTERS[index],accidental:m[2] || '',octave:Number(m[3]),index,
    diatonic:Number(m[3])*7+index,midi:Number(m[3])*12+NATURAL[index]+alteration};
}
export function normalizeInversionPitch(pitch) {
  const p = parseInversionPitch(pitch);
  return p ? `${p.letter}${p.accidental === 'n' ? '' : p.accidental}${p.octave}` : '';
}
export function getIntervalInversionPair(pair = {}, placement = 'below') {
  const request = parseRequestedInterval(pair.interval);
  const original = parseInversionPitch(pair.given);
  if (!request || request.size > 8 || !original) throw Error('Choose a given note and an interval of an octave or less.');
  const built = transposeRequestedInterval(pair.given, request, 'above');
  const upper = parseInversionPitch(built);
  const lower = original;
  const inverted = placement === 'above'
    ? [built, `${lower.letter}${lower.accidental}${lower.octave+1}`]
    : [`${upper.letter}${upper.accidental}${upper.octave-1}`, pair.given];
  if (inverted.some(p=>!parseInversionPitch(p))) throw Error('Inversion falls outside the supported staff range.');
  const quality = INVERT_QUALITY[request.quality], size = 9 - request.size;
  return {given:normalizeInversionPitch(pair.given), built:normalizeInversionPitch(built),inverted:inverted.map(normalizeInversionPitch),
    quality, size, label:`${quality === 'MIN'?'min':quality==='DIM'?'dim':quality} ${size===1?'1st':size===2?'2nd':size===3?'3rd':size===8?'8ve':size+'th'}`};
}
function sameSpellingAndPitchClass(left, right) {
  const a=parseInversionPitch(left),b=parseInversionPitch(right);
  return !!(a&&b&&a.letter===b.letter&&((a.midi%12)+12)%12===((b.midi%12)+12)%12);
}
export function matchingInvertedNotes(expected, received) {
  if (!Array.isArray(received) || received.length!==2) return false;
  const entries=received.map(parseInversionPitch);
  if(entries.some(p=>!p)) return false;
  const [lo,hi]=entries.sort((a,b)=>a.diatonic-b.diatonic);
  if(hi.diatonic-lo.diatonic!==expected.size-1 || hi.midi < lo.midi) return false;
  // A correct inversion retains both pitch spellings and reverses their order;
  // either octave placement of the inverted interval is accepted.
  const [expectedLo,expectedHi]=expected.inverted;
  return sameSpellingAndPitchClass(`${lo.letter}${lo.accidental}${lo.octave}`,expectedLo) &&
    sameSpellingAndPitchClass(`${hi.letter}${hi.accidental}${hi.octave}`,expectedHi) &&
    (hi.midi - lo.midi === Math.abs(parseInversionPitch(expectedHi).midi - parseInversionPitch(expectedLo).midi));
}
export function gradeIntervalInversion(config,answer={}) {
  const rows=config.intervalInversion?.pairs||[], maxScore=Number(config.points)||0;
  const parts={},deductions=[];
  const perPiece=rows.length ? maxScore/(rows.length*3) : 0;
  rows.forEach((pair,i)=>{
    let expected;
    try {expected=getIntervalInversionPair(pair,config.intervalInversion?.placement||'below');}
    catch {expected=null;}
    const got=answer.intervalAnswers?.[i] || {};
    const constructed=!!expected && normalizeInversionPitch(got.build)===expected.built;
    const inverted=!!expected && matchingInvertedNotes(expected,got.invert);
    const qualityCorrect=!!expected && String(got.quality||'').toUpperCase()===expected.quality;
    const sizeCorrect=!!expected && Number(got.size)===expected.size;
    const prefix=`pair-${i+1}`;
    if(!constructed)deductions.push({key:`${prefix}-build`,label:`Incorrect constructed interval in example ${i+1}`,points:perPiece});
    if(!inverted)deductions.push({key:`${prefix}-invert`,label:`Incorrect inversion in example ${i+1}`,points:perPiece});
    if(!qualityCorrect)deductions.push({key:`${prefix}-quality`,label:`Incorrect inversion type in example ${i+1}`,points:perPiece/2});
    if(!sizeCorrect)deductions.push({key:`${prefix}-size`,label:`Incorrect inversion size in example ${i+1}`,points:perPiece/2});
    parts[prefix]={correct:constructed&&inverted&&qualityCorrect&&sizeCorrect, constructed, inverted,qualityCorrect,sizeCorrect,
      expected,received:{build:got.build||null,invert:got.invert||[],quality:got.quality||null,size:got.size||null}};
  });
  const score=Math.max(0,Math.min(maxScore,maxScore-deductions.reduce((v,d)=>v+d.points,0)));
  return {score,maxScore,correct:deductions.length===0,deductions,parts};
}

// Adapt construction/inversion task data to the standard StaffInput schema.
// All drawing, hit testing, palettes and selectors belong to StaffInput.
export function intervalInversionStaffConfig(config) {
  const pairs = config.intervalInversion?.pairs || [];
  const givenNotes = {}, prompts = [], selectorSlots = [], limits = {};
  pairs.forEach((pair, index) => {
    const build = index * 2 + 1, invert = build + 1;
    givenNotes[build] = {symbol:'whole_note', pitches:[pair.given]};
    prompts.push({measure:build, text:formatInversionIntervalLabel(pair.interval)});
    selectorSlots.push(invert);
    limits[build] = 1;
    limits[invert] = 2;
  });
  return {...config, inputMode:'placeNotes', measures:pairs.length*2,
    slotCount:pairs.length*2, slotsPerBar:1, slotWidth:config.slotWidth ?? 205,
    maxNotesPerSlot:2, maxNotesPerSlotBySlot:limits,
    allowedSymbols:['whole_note'], allowLedgerLines:true, lockGivenNotes:true,
    allowKeySignatureInput:false, showTonic:false, renderBarlines:true,
    givenNotes, prompts, promptPosition:'below',
    staffInputPolicy:{allowNoteInput:true, allowAccidentalInput:true,
      allowTonicInput:false, allowHalfStepInput:false, accidentalInputMode:'palette'},
    intervalSelects:{slots:selectorSlots, qualities:INVERSION_QUALITY_OPTIONS,
      sizes:['Uni','2nd','3rd','4th','5th','6th','7th','8ve']}};
}
const inversionSizeLabel = size => Number(size) === 1 ? 'Uni' : Number(size) === 8 ? '8ve'
  : Number(size) ? Number(size) + ({2:'nd',3:'rd'}[Number(size)] || 'th') : '';
function inversionStaffNote(pitch) {
  const parsed = parseInversionPitch(pitch);
  return parsed ? {symbol:'whole_note', pitch:parsed.letter+parsed.octave} : null;
}
export function intervalInversionStaffValue(value = {}, config) {
  const notes = {}, noteAccidentals = {}, intervalSelections = {};
  (config.intervalInversion?.pairs || []).forEach((_, index) => {
    const row = value.intervalAnswers?.[index] || {};
    const build = index*2+1, invert = build+1;
    const built = inversionStaffNote(row.build);
    if (built) {
      notes[build] = built;
      noteAccidentals[build] = parseInversionPitch(row.build).accidental;
    }
    notes[invert] = (row.invert || []).map(inversionStaffNote).filter(Boolean);
    noteAccidentals[invert] = Object.fromEntries((row.invert || []).map((pitch,i)=>[i,parseInversionPitch(pitch)?.accidental || '']));
    intervalSelections[invert] = {quality:row.quality || '',size:inversionSizeLabel(row.size)};
  });
  return {...value, notes, noteAccidentals, intervalSelections};
}
export function intervalInversionFromStaffValue(next, previous = {}, config) {
  const intervalAnswers = {...previous.intervalAnswers};
  const pitches = slot => {
    const raw = next.notes?.[slot];
    const notes = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return notes.map((note,i) => {
      const parsed = parseInversionPitch(note.pitch);
      if (!parsed) return '';
      const bank = next.noteAccidentals?.[slot];
      const accidental = (typeof bank === 'string' ? bank : bank?.[i]) ?? parsed.accidental;
      return normalizeInversionPitch(parsed.letter+accidental+parsed.octave);
    }).filter(Boolean);
  };
  (config.intervalInversion?.pairs || []).forEach((_,index)=>{
    const build = index*2+1, invert = build+1;
    const selection = next.intervalSelections?.[invert] || {};
    intervalAnswers[index] = {...intervalAnswers[index], build:pitches(build)[0] || '',
      invert:pitches(invert), quality:selection.quality || '',
      size:selection.size === 'Uni' ? '1' : String(selection.size || '').match(/^\d+/)?.[0] || ''};
  });
  return {...previous, intervalAnswers};
}
export function intervalInversionStaffFeedback(feedback, config) {
  if (!feedback) return feedback;
  const parts = {...feedback.parts};
  (config.intervalInversion?.pairs || []).forEach((_,index)=>{
    const pair = feedback.parts?.[`pair-${index+1}`];
    if (!pair) return;
    parts[index*2+1] = {correct:pair.constructed, expected:pair.expected?.built};
    parts[index*2+2] = {correct:pair.inverted,
      pitchCorrect:pair.inverted, qualityCorrect:pair.qualityCorrect, sizeCorrect:pair.sizeCorrect,
      expected:pair.expected?.inverted};
  });
  return {...feedback, parts};
}
